import type { BaseEventListenerProps } from './AbstractEventListener';
import { AbstractEventListener } from './AbstractEventListener';
import type { BasePermission, EmptyObject, LoggedEvent } from './loggingTypes';

export type MultipleDataLayerEvents<T = unknown> = {
  type: 'multiple';
  events: T[];
};

/**
 * Any DataLayer driven logger like GA4 or GTM require the user to accept Performance tracking in
 * GDPR regions. So this permission is baked into this implementation.
 */
export type DataLayerLoggingPermissions = BasePermission & {
  performance: boolean;
};

/**
 * A logger that simply passes events to the dataLayer variable.
 *
 * This is how Google Analytics and Google Tag Manager works.
 */
export type DataLayerEventListenerProps<LoggingContext, OtherEvents, GoogleEventType> = {
  /**
   * Formats the events into the shape that the particular GTM/GA implementation unerstands.
   *
   * Note that because dataLayer accepts arrays as items, we can't return a regular array here for
   * multiple events. So you have to wrap them in: { type: 'multiple', events: [...events...]} when
   * returning.
   */
  eventFormatter: (
    event: LoggedEvent<OtherEvents>,
    context: Partial<LoggingContext>
  ) => MultipleDataLayerEvents<GoogleEventType> | GoogleEventType | null | undefined;
} & BaseEventListenerProps<LoggingContext, OtherEvents>;

/** Rudimentary event listener that logs to the console. */
export abstract class AbstractDataLayerEventListener<
  LoggingContext extends EmptyObject = EmptyObject,
  CustomEvents extends EmptyObject = EmptyObject,
  Permission extends DataLayerLoggingPermissions = DataLayerLoggingPermissions,
  GoogleEvent extends EmptyObject = EmptyObject
> extends AbstractEventListener<LoggingContext, CustomEvents, Permission> {
  private readonly props: DataLayerEventListenerProps<
    LoggingContext,
    LoggedEvent<CustomEvents>,
    GoogleEvent
  >;
  private eventCallbacks: Promise<void>[] = [];

  constructor(
    props: DataLayerEventListenerProps<LoggingContext, LoggedEvent<CustomEvents>, GoogleEvent>
  ) {
    super({
      ...props,
      requiredPermissions: ['logging', 'performance'],
    });
    this.props = props;
  }

  /** Adds an event to the dataLayer variable. This is handled differently between GA4 and GTM (FFS) */
  protected abstract addEventToDataLayer(event: GoogleEvent): void;

  /**
   * Adds the event callback to an event.
   *
   * Returns whether the callback was added.
   */
  protected abstract addEventCallback: (event: GoogleEvent, callback: () => void) => boolean;

  /** @override */
  protected logEventInternal = (
    event: LoggedEvent<CustomEvents>,
    context: Partial<LoggingContext>
  ): void => {
    const formattedEvent = this.props.eventFormatter(event, context);
    if (!formattedEvent) return;

    const hasMultipleEvents =
      typeof formattedEvent === 'object' &&
      'type' in formattedEvent &&
      // @ts-ignore Just checked on the line above.
      formattedEvent.type === 'multiple';

    if (hasMultipleEvents) {
      const formattedEvents = formattedEvent;

      for (const singleFormattedEvent of formattedEvents.events) {
        this.logGoogleEvent(singleFormattedEvent);
      }
    } else {
      this.logGoogleEvent(formattedEvent as GoogleEvent);
    }
  };

  /**
   * Helper function for writting a single event to the dataLayer.
   *
   * Adds listeners for event log completion so we can track when they're all done for the {flush}
   * implementation.
   */
  protected logGoogleEvent = (event: GoogleEvent): void => {
    // If not an event object, skip this value.
    if (!event || typeof event !== 'object') {
      return;
    }

    let onEventLoggedCallback: (value: void | PromiseLike<void>) => void;

    const onEventLogged = new Promise<void>(resolve => {
      onEventLoggedCallback = resolve;
    });

    const callbackAdded = this.addEventCallback(event, onEventLoggedCallback!);

    callbackAdded && this.eventCallbacks.push(onEventLogged);
    this.addEventToDataLayer(event);
  };

  /**
   * Waits for all events in the dataLayer to be flagged as delivered (via event callback API)
   * before resolving.
   *
   * @override
   */
  protected flushInternal: () => Promise<unknown> = async () => {
    const resolvedCount = this.eventCallbacks.length;
    await Promise.allSettled(this.eventCallbacks);

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.eventCallbacks = this.eventCallbacks.slice(resolvedCount);

    return;
  };
}
